import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { useMainCompany } from '../../context/MainCompanyContext';
import AdminPageHeader from '../../components/common/AdminPageHeader';
import Toast from '../../components/common/Toast';
import './GlobalStateSetting.css';
import api from '../../api/axios';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

export default function GlobalStateSetting() {
  const { selectedMain } = useMainCompany();
  const [stateid, setstateid] = useState(0);
  const [countyid, setcountyid] = useState(0);
  const [type, setType] = useState('buyer_proration');
  const [months, setMonths] = useState(Array(12).fill(''));
  // support multiple rows (different methodid) returned from API
  const [rows, setRows] = useState([]); // each row: { id, methodid, countyid, stateid, type, jan..dec }


  const [toast, setToast] = useState(null);
  // auto-hide toast after shown
  useEffect(() => {
    if (!toast || !toast.visible) return;
    const t = setTimeout(() => setToast(null), 2500);
    return () => clearTimeout(t);
  }, [toast]);
  const [states, setStates] = useState([]);
  const [counties, setCounties] = useState([]);
  const [loadingStates, setLoadingStates] = useState(false);
  const [loadingCounties, setLoadingCounties] = useState(false);
  // whether this state uses county-based tax settings (fetched from server)
  const [countyBasedTax, setCountyBasedTax] = useState(null); // null = unknown, true/false = known

  // If type is buyer_impound we ignore methodid and countyid in UI — so we disable county input
  const countyDisabled = type === 'buyer_impound';

  // whether the currently selected state uses county-based settings (determined by server)
  const requiresCountyByName = countyBasedTax === true; // dynamic replacement for previous static list

  // For buyer_proration and seller_proration, if the selected state has counties (or is in the special list), county selection should be mandatory.
  // Only require county when the county dropdown is actually shown in the UI
  const showCountySelect = (type !== 'buyer_impound') && requiresCountyByName;
  const countyRequiredForProration = (type === 'buyer_proration' || type === 'seller_proration') && showCountySelect;

  const isValid = useMemo(() => {
    const hasState = !!stateid;
    const hasType = !!type;
    const hasMonth = months.some(v => v !== '' && v !== null && v !== undefined);
    // county logic:
    // - buyer_impound: ignore county (not required)
    // - proration types with counties: county is required
    // - otherwise county is optional
    let hasCounty = true;
    if (countyDisabled) hasCounty = true;
    //else if (countyRequiredForProration) hasCounty = !!countyid;
    else hasCounty = true;

    return hasState && hasCounty && hasType && hasMonth;
  }, [stateid, type, months, countyDisabled]);

  function onMonthChange(i, val) {
    const v = val.replace(/[^0-9-]/g, '');
    const copy = months.slice();
    copy[i] = v;
    setMonths(copy);
  }

  useEffect(() => {
    let mounted = true;
    async function loadStates() {
      setLoadingStates(true);
      try {
        // reset dependent selections when main company changes
        setstateid(0);
        setcountyid(0);
        const params = {};
        if (selectedMain) params.maincompanyid = selectedMain;
        const resp = await api.get('/admin/states', { params });
        if (!mounted) return;
        // normalize response: API may return array or object wrapper
        const payload = resp && (resp.data ?? resp);
        const list = Array.isArray(payload) ? payload : (Array.isArray(payload?.data) ? payload.data : []);
        setStates(list);
      } catch (e) {
        console.error('Failed to load states', e);
      } finally { setLoadingStates(false); }
    }
    loadStates();
    return () => { mounted = false };
  }, [selectedMain]);

  useEffect(() => {
    // When state changes, reset county state and query server to determine whether
    // this state uses county-based tax settings.
    // clear dependent UI immediately to avoid showing stale data
    setCounties([]);
    setcountyid(0);
    setRows([]);
    setMonths(Array(12).fill(''));
    setCountyBasedTax(null);
    if (!stateid) return;

    let mounted = true;
    const checkCountyBased = async () => {
      setLoadingCounties(true);
      try {
        const body = { stateid };
        if (selectedMain) body.maincompanyid = selectedMain;
        // POST to server to determine county-based tax
        const resp = await api.post('/admin/state-setting/county-based-tax', body);
        if (!mounted) return;
        // response may be { data: { county_based_tax: true } } or { county_based_tax: true }
        const cb = resp?.data?.data?.county_based_tax ?? resp?.data?.county_based_tax ?? false;
        setCountyBasedTax(!!cb);

        // If server says county-based, fetch counties for the state
        if (cb) {
          try {
            const params = { stateid };
            if (selectedMain) params.maincompanyid = selectedMain;
            const cresp = await api.get('/auth/tax-counties', { params });
            if (!mounted) return;
            const payload = cresp && (cresp.data ?? cresp);
            const list = Array.isArray(payload) ? payload : (Array.isArray(payload?.data) ? payload.data : []);
            setCounties(list);
          } catch (err) {
            console.error('Failed to load counties after county-based check', err);
            setCounties([]);
          }
        } else {
          // Not county based: ensure counties are empty
          setCounties([]);
        }
        const params = { stateid: stateid, type, countyid };
        if (selectedMain) params.maincompanyid = selectedMain;
        const settingResp = await api.get('/admin/state-setting', { params });
        const settingBody = settingResp.data;
        const rows = settingBody && Array.isArray(settingBody.data) ? settingBody.data : (Array.isArray(settingBody) ? settingBody : (settingBody && Array.isArray(settingBody.data) ? settingBody.data : []));
        if (rows && rows.length > 0) {
          setRows(rows);
          const r = rows[0];
          const mm = [r.jan, r.feb, r.mar, r.apr, r.may, r.jun, r.jul, r.aug, r.sep, r.oct, r.nov, r.dec].map(v => (v === null || v === undefined) ? '' : String(v));
          setMonths(mm);
        } else {
          setRows([]);
          setMonths(Array(12).fill(''));
        }
      } catch (err) {
        console.error('Failed to check county-based tax for state', err);
        // fallback to false so UI continues
        setCountyBasedTax(false);
        setCounties([]);
      } finally {
        if (mounted) setLoadingCounties(false);
      }
    };

    checkCountyBased();
    return () => { mounted = false };
  }, [stateid, selectedMain, countyid, type]);

  // fetch existing setting for the selected filters (state, county, type)
  // Extracted fetch logic so we can reuse after saves
  const fetchSettings = useCallback(async function fetchSettings() {
    try {
      // pre-checks: need state and type
      if (!stateid || !type) return;
      // wait until we know whether the state is county-based
      //if (countyBasedTax === null) return;
      
      // If county selection is required (proration types and state has counties) but not selected, clear rows and don't fetch
      /* if (countyRequiredForProration && !countyid) {
        setRows([]);
        setMonths(Array(12).fill(''));
        return;
        } */
       
       // If the state is county-based, fetch per-county settings only when a county is selected.
       if (countyBasedTax === true) {
         /* if (!countyid) {
           // no county selected yet: clear UI
           setRows([]);
           setMonths(Array(12).fill(''));
           return;
          } */
          console.log('Fetching settings for', { stateid, countyid, type, countyRequiredForProration, countyBasedTax });
        // county-based and county selected: fetch per-county settings
        const params = { stateid: stateid, type, countyid };
        if (selectedMain) params.maincompanyid = selectedMain;
        const resp = await api.get('/admin/state-setting', { params });
        const body = resp.data;
        const rows = body && Array.isArray(body.data) ? body.data : (Array.isArray(body) ? body : (body && Array.isArray(body.data) ? body.data : []));
        if (rows && rows.length > 0) {
          setRows(rows);
          const r = rows[0];
          const mm = [r.jan, r.feb, r.mar, r.apr, r.may, r.jun, r.jul, r.aug, r.sep, r.oct, r.nov, r.dec].map(v => (v === null || v === undefined) ? '' : String(v));
          setMonths(mm);
        } else {
          setRows([]);
          setMonths(Array(12).fill(''));
        }
        return;
      }

      // countyBasedTax === false: fetch state-level settings (no countyid)
      const params = { stateid: stateid, type };
      if (selectedMain) params.maincompanyid = selectedMain;
      const resp = await api.get('/admin/state-setting', { params });
      const body = resp.data;
      const rows = body && Array.isArray(body.data) ? body.data : (Array.isArray(body) ? body : (body && Array.isArray(body.data) ? body.data : []));
      if (rows && rows.length > 0) {
        setRows(rows);
        const r = rows[0];
        const mm = [r.jan, r.feb, r.mar, r.apr, r.may, r.jun, r.jul, r.aug, r.sep, r.oct, r.nov, r.dec].map(v => (v === null || v === undefined) ? '' : String(v));
        setMonths(mm);
      } else {
        setRows([]);
        setMonths(Array(12).fill(''));
      }
    } catch (e) {
      console.error('Failed to load state setting', e);
    }
  }, [stateid, countyid, type, countyRequiredForProration, selectedMain, countyBasedTax]);

  useEffect(() => {
    // call fetchSettings when relevant inputs change
    fetchSettings();
  }, [fetchSettings]);

  function onSave() {
    if (!isValid)
      return setToast({ type: 'error', message: 'Please fill required fields' });

    const payload = [];

    if (rows && rows.length > 0) {
      for (const r of rows) {
        const item = {
          id: r.id,
          stateid: Number(stateid),
          methodid: (r.methodid ?? 'a'),
          type,
          jan: Number(r.jan) || 0,
          feb: Number(r.feb) || 0,
          mar: Number(r.mar) || 0,
          apr: Number(r.apr) || 0,
          may: Number(r.may) || 0,
          jun: Number(r.jun) || 0,
          jul: Number(r.jul) || 0,
          aug: Number(r.aug) || 0,
          sep: Number(r.sep) || 0,
          oct: Number(r.oct) || 0,
          nov: Number(r.nov) || 0,
          dec: Number(r.dec) || 0,
        };

        // ✅ Always include countyid — fallback to 0 if not applicable
        if (!countyDisabled) {
          item.countyid =
            (r.countyid ?? countyid ?? 0) ||
            0; // ensure numeric 0 even if undefined/null
        } else {
          item.countyid = 0;
        }

        payload.push(item);
      }
    } else {
      // no existing rows
      const base = {
        stateid: Number(stateid),
        methodid: 'a',
        type,
        jan: Number(months[0]) || 0,
        feb: Number(months[1]) || 0,
        mar: Number(months[2]) || 0,
        apr: Number(months[3]) || 0,
        may: Number(months[4]) || 0,
        jun: Number(months[5]) || 0,
        jul: Number(months[6]) || 0,
        aug: Number(months[7]) || 0,
        sep: Number(months[8]) || 0,
        oct: Number(months[9]) || 0,
        nov: Number(months[10]) || 0,
        dec: Number(months[11]) || 0,
        countyid: !countyDisabled ? (countyid || 0) : 0, // ✅ Always included
      };
      payload.push(base);
    }

    (async () => {
      try {
        const id = rows && rows.length > 0 && rows[0].id ? rows[0].id : 0;

        // attach maincompanyid to each payload item
        if (Array.isArray(payload)) {
          for (const p of payload) {
            if (selectedMain) p.maincompanyid = selectedMain;
          }
        } else {
          if (selectedMain) payload.maincompanyid = selectedMain;
        }

        await api.put(`/admin/state-setting/${id}`, payload);

        const humanType = (type || '')
          .replace(/_/g, ' ')
          .replace(/\b\w/g, (c) => c.toUpperCase());
        const stateObj = states.find(
          (s) =>
            String(s.id) === String(stateid) ||
            String(s.stateid) === String(stateid) ||
            String(s.value) === String(stateid)
        );
        const countyObj = counties.find(
          (c) =>
            String(c.id) === String(countyid) ||
            String(c.countyid) === String(countyid) ||
            String(c.value) === String(countyid)
        );
        const stateName =
          stateObj?.statename ??
          stateObj?.name ??
          stateObj?.state ??
          stateObj?.label ??
          stateObj?.value;
        const countyName =
          countyObj?.countyname ??
          countyObj?.name ??
          countyObj?.county ??
          countyObj?.label ??
          countyObj?.value;

        const suffix = stateName
          ? ` for ${stateName}${countyName ? ` / ${countyName}` : ''}`
          : '';
        setToast({
          type: 'success',
          message: `State setting for ${humanType}${suffix} updated successfully`,
          visible: true,
        });
        await fetchSettings();
      } catch (err) {
        console.error('Save failed', err);
        setToast({
          type: 'error',
          message: err?.response?.data?.message || 'Save failed',
          visible: true,
        });
      }
    })();
  }


  return (
    <div className="admin-page global-state-setting">
      <AdminPageHeader title="Global State Setting" />

      <div className="form-row">
        <select value={stateid} onChange={e => { setstateid(Number(e.target.value) || 0); setcountyid(0); }} className="border px-2 py-1 rounded text-sm bg-white text-black w-56">
          <option value={0}>Select State</option>
          {loadingStates && <option value={0}>Loading...</option>}
          {!loadingStates && Array.isArray(states) && states.map(s => (
            <option key={s.id ?? s.stateid ?? s.value} value={s.id ?? s.stateid ?? s.value}>{s.statename ?? s.name ?? s.state ?? s.label ?? s.value}</option>
          ))}
        </select>

        {/* Show county select only for special states and not for buyer_impound */}
        {type !== 'buyer_impound' && requiresCountyByName ? (
          <>
            <select value={countyid} onChange={e => setcountyid(Number(e.target.value) || 0)} disabled={countyDisabled || loadingCounties} className="border px-2 py-1 rounded text-sm bg-white text-black w-56">
              <option value={0}>{countyDisabled ? 'N/A' : 'All Counties'}</option>
              {loadingCounties && <option value={0}>Loading...</option>}
              {!loadingCounties && Array.isArray(counties) && counties.map(c => (
                <option key={c.id ?? c.countyid ?? c.value} value={c.id ?? c.countyid ?? c.value}>{c.countyname ?? c.name ?? c.county ?? c.label ?? c.value}</option>
              ))}
            </select>

            {/* Inline help when county is required but not selected */}
            {countyRequiredForProration && !countyid && (
              <div className="field-note">Choose a county to view/edit proration settings</div>
            )}
          </>
        ) : null}
      </div>


      <div className="tabs" role="tablist" aria-label="Type">
        {['buyer_proration', 'seller_proration', 'buyer_impound'].map(t => (
          <button
            key={t}
            role="tab"
            aria-selected={type === t}
            className={type === t ? 'active' : ''}
            onClick={() => setType(t)}
          >{t.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}</button>
        ))}
      </div>

      {/* Render rows as blocks: method label row, month labels row, inputs row */}
      {rows && rows.length > 0 ? (
        rows.map((r, idx) => (
          <div key={r.id ?? idx} className="record-block">
            {(() => {
              const label = r.methodid;
              return label ? <div className="method-label">{label}</div> : null;
            })()}

            <div className="months-grid-wrap max-w-full overflow-x-auto">
              <div style={{ minWidth: 680 }}>
                <div className="months-grid">
                  {MONTHS.map(m => (
                    <div key={m} className="month-label">{m}</div>
                  ))}
                </div>

                <div className="months-input-row">
                  {MONTHS.map((m, i) => (
                    <input key={m} className="month-input" value={(r[['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec'][i]] ?? '')} onChange={e => {
                      const v = e.target.value.replace(/[^0-9-]/g, '');
                      const copy = rows.slice();
                      copy[idx] = { ...copy[idx], [['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec'][i]]: v };
                      setRows(copy);
                    }} />
                  ))}
                </div>
              </div>
            </div>
          </div>
        ))
      ) : (
        <div className="record-block">

          <div className="months-grid-wrap max-w-full overflow-x-auto">
            <div style={{ minWidth: 680 }}>
              <div className="months-grid">
                {MONTHS.map(m => (
                  <div key={m} className="month-label">{m}</div>
                ))}
              </div>
              <div className="months-input-row">
                {MONTHS.map((m, i) => (
                  <input key={m} className="month-input" value={months[i]} onChange={e => onMonthChange(i, e.target.value)} />
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="actions-row">
        <button className="px-3 py-2 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-60" disabled={!isValid} onClick={onSave}>Save</button>
      </div>

      {toast && <Toast type={toast.type} message={toast.message} visible={toast.visible} onClose={() => setToast(null)} />}
    </div>
  );
}
